import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Body3Regular, CaptionLight, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import { Col, concatDateAndTimeDayjs, isSameDate, Row } from '../../../lib/utils';
import SvgIcon from '../../SvgIcon';
import InputSquareDropDown from '../../DropDown/InputSquareDropDown';
import Calendar from '../../Calendar/Calendar';
import ColorSquareButton from '../../Button/ColorSquareButton';
import WhiteRoundButton from '../../Button/WhiteRoundButton';
import {
    createNewOpportunityAPI,
    getNewOpportunityCategoriesAPI,
    getNewOpportunityMemberMatchAPI,
} from '../../../api/newOpportunity';
import {
    calculateCursorAfterFormat,
    formatNewOpportunityPhoneNumber,
    normalizeNewOpportunityPhoneDigits,
} from '../../../utils/newOpportunityPhone';

type RefineDate = {
    refineStartTime: string;
    refineEndTime: string;
};

type Category = {
    _id?: string;
    id?: string;
    name: string;
    isActive?: boolean;
};

const getId = (item: Category) => item?._id || item?.id || '';
const limitMemberName = (value: string) => value.slice(0, 5);
const FORM_FIELD_WIDTH = 420;
const SECTION_WIDTH = 520;

const inputStyle: React.CSSProperties = {
    width: FORM_FIELD_WIDTH,
    height: 56,
    border: '1px solid #A6BFEF',
    borderRadius: 12,
    marginTop: 14,
    fontSize: 18,
    paddingLeft: 18,
    paddingRight: 18,
    outline: 'none',
    backgroundColor: colors.WHITE_50,
    color: colors.LAVEL_4,
    boxSizing: 'border-box',
};

const sectionStyle: React.CSSProperties = {
    width: SECTION_WIDTH,
    padding: '32px 36px 38px',
    border: '1px solid #E1E9F7',
    borderRadius: 18,
    backgroundColor: colors.WHITE_50,
    boxSizing: 'border-box',
};

const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 32,
};

const sectionNumberStyle: React.CSSProperties = {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: '#EDF4FF',
    color: colors.BLUE_500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    fontWeight: 800,
};

const badgeStyle = (type: 'required' | 'optional' | 'conditional'): React.CSSProperties => ({
    minWidth: type === 'conditional' ? 88 : 40,
    height: 24,
    padding: '0 8px',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
    color: type === 'optional' ? '#64748B' : type === 'conditional' ? '#7C3AED' : '#DC2626',
    backgroundColor: type === 'optional' ? '#F1F5F9' : type === 'conditional' ? '#F3E8FF' : '#FEF2F2',
});

const fieldBlockStyle: React.CSSProperties = {
    marginTop: 34,
};

const requiredLabelMap = {
    required: '필수',
    optional: '선택',
    conditional: '신규 시 필수',
};

const FieldLabel = ({
    label,
    type = 'required',
    help,
}: {
    label: string;
    type?: 'required' | 'optional' | 'conditional';
    help?: string;
}) => {
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    return (
        <Col>
            <Row style={{ alignItems: 'center', gap: 8 }}>
                <Body3Regular style={{ color: colors.LAVEL_4, fontSize: 18, fontWeight: 800 }}>{label}</Body3Regular>
                <span style={badgeStyle(type)}>{requiredLabelMap[type]}</span>
                {help && (
                    <span
                        style={{ position: 'relative', display: 'inline-flex' }}
                        onMouseEnter={() => setIsHelpOpen(true)}
                        onMouseLeave={() => setIsHelpOpen(false)}
                    >
                        <button
                            type="button"
                            aria-label={`${label} 도움말`}
                            onClick={(event) => {
                                event.stopPropagation();
                                setIsHelpOpen((prev) => !prev);
                            }}
                            onFocus={() => setIsHelpOpen(true)}
                            onBlur={() => setIsHelpOpen(false)}
                            style={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                border: 0,
                                backgroundColor: '#F1F5F9',
                                color: '#64748B',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 13,
                                fontWeight: 900,
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            ?
                        </button>
                        {isHelpOpen && (
                            <span
                                style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: 30,
                                    transform: 'translateX(-50%)',
                                    width: 240,
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    backgroundColor: '#1F2937',
                                    color: colors.WHITE_50,
                                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: '19px',
                                    whiteSpace: 'normal',
                                    zIndex: 3000,
                                }}
                            >
                                {help}
                            </span>
                        )}
                    </span>
                )}
            </Row>
        </Col>
    );
};

const CreateNewOpportunitySchedule = ({
    coachOption,
    authorization,
    authObject,
    createAlert,
    selectedSchedule,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
}: any) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const currentTime = selectedSchedule ? dayjs(selectedSchedule?.scheduleStartTime) : dayjs();
    const roundedTime = currentTime.minute(Math.floor(currentTime.minute() / 10) * 10).second(0);
    const currentTimeAdd50 = dayjs(roundedTime).add(50, 'minute');

    const phoneInputRef = useRef<HTMLInputElement>(null);

    const [coach, setCoach] = useState({
        value: selectedSchedule ? selectedSchedule?.coachId : '',
        label: selectedSchedule ? selectedSchedule?.coachName : '강사를 선택해주세요!',
    });
    const [categoryId, setCategoryId] = useState('');
    const [memberName, setMemberName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [memo, setMemo] = useState('');
    const [isSendAlimtalkToMember, setIsSendAlimtalkToMember] = useState(true);
    const [startTime, setStartTime] = useState({ value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') });
    const [endTime, setEndTime] = useState({
        value: currentTimeAdd50,
        label: dayjs(currentTimeAdd50).format('A hh:mm'),
    });
    const [timeList, setTimeList] = useState<any>(null);
    const [selectedDay, setSelectedDay] = useState<any>(
        selectedSchedule ? new Date(selectedSchedule?.scheduleStartTime) : new Date()
    );
    const [isCalendar, setIsCalendar] = useState<boolean>(false);

    const categoryQuery = useQuery({
        queryKey: ['newOpportunityCategories', authObject?.centerId],
        queryFn: () => getNewOpportunityCategoriesAPI(authObject?.centerId, authorization),
        enabled: !!authObject?.centerId && !!authorization,
    });

    const categories: Category[] = categoryQuery.data?.data?.results || [];
    const activeCategories = categories.filter((category) => category.isActive !== false);
    const selectedCategory = useMemo(
        () => activeCategories.find((category) => getId(category) === categoryId),
        [activeCategories, categoryId]
    );
    const phoneDigits = normalizeNewOpportunityPhoneDigits(phoneNumber);
    const memberMatchQuery = useQuery({
        queryKey: ['newOpportunityMemberMatch', authObject?.centerId, phoneDigits],
        queryFn: () => getNewOpportunityMemberMatchAPI(authObject?.centerId, authorization, phoneNumber),
        enabled: !!authObject?.centerId && !!authorization && phoneDigits.length === 11,
        staleTime: 0,
    });
    const matchedMember = memberMatchQuery.data?.data?.matched ? memberMatchQuery.data?.data?.member : null;
    const handleCoachSelect = (option: any) => {
        setCoach(option);
    };

    const handleStartTimeSelect = (option: any) => {
        setStartTime(option);
    };

    const handleEndTimeSelect = (option: any) => {
        setEndTime(option);
    };

    const onClickAddTime = (addNumber: number) => {
        if (
            dayjs(startTime.value).format('YYYY-MM-DD') ===
            dayjs(endTime.value).add(addNumber, 'minute').format('YYYY-MM-DD')
        ) {
            setEndTime({
                value: dayjs(endTime.value).add(addNumber, 'minute'),
                label: dayjs(dayjs(endTime.value).add(addNumber, 'minute')).format('A hh:mm'),
            });
        } else {
            createAlert('입력 오류', '추가 가능한 시간을 초과하였습니다', '확인', () => {});
        }
    };

    const onClickSubTime = (subNumber: number) => {
        if (dayjs(startTime.value).isBefore(dayjs(endTime.value).subtract(subNumber, 'minute'))) {
            setEndTime({
                value: dayjs(endTime.value).subtract(subNumber, 'minute'),
                label: dayjs(dayjs(endTime.value).subtract(subNumber, 'minute')).format('A hh:mm'),
            });
        } else {
            createAlert('입력 오류', '감소 가능한 시간을 초과하였습니다', '확인', () => {});
        }
    };

    const createNewOpportunity = useMutation({
        mutationFn: async (refineDate: RefineDate) => {
            const targetMemberName = matchedMember?.name || memberName.trim();
            return await createNewOpportunityAPI(authObject?.centerId, authorization, {
                coachId: coach.value,
                memberId: matchedMember?.id || matchedMember?._id,
                memberName: targetMemberName,
                phoneNumber: formatNewOpportunityPhoneNumber(phoneNumber),
                categoryId,
                name: `${selectedCategory?.name || '신규기회'} - ${targetMemberName}`,
                startTime: refineDate.refineStartTime,
                endTime: refineDate.refineEndTime,
                memo: memo.trim(),
                isSendAlimtalkToMember,
            });
        },
        onSuccess: (res, refineDate) => {
            if (res.status === 201) {
                queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] });
                queryClient.invalidateQueries({ queryKey: ['newOpportunities'] });
                queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] });
                // 신규기회로 인입 회원이 생성될 수 있으므로 회원목록/통계도 무효화
                queryClient.invalidateQueries({ queryKey: ['publicMember'] });
                queryClient.invalidateQueries({ queryKey: ['memberStats'] });
                navigate('/schedule', {
                    state: {
                        calendarType,
                        calendarTimeInfo: refineDate.refineStartTime,
                        calendarCoachId: coach.value,
                    },
                    replace: true,
                });
            } else {
                createAlert('오류', '신규기회를 등록하지 못했습니다.', true);
            }
        },
        onError: (error: any) => {
            const status = error?.response?.status;
            if (status === 410 || status === 420) {
                createAlert('등록 불가', '이미 예약된 시간입니다.', true);
                return;
            }
            createAlert('오류', '신규기회를 등록하지 못했습니다. 입력값을 다시 확인해주세요.', true);
        },
    });

    const onClickCreateOpportunity = () => {
        if (coach?.value === '') {
            createAlert('필수 입력', '담당 강사를 선택해주세요', true);
            return;
        }
        if (!categoryId) {
            createAlert('필수 입력', '신규기회 유형을 선택해주세요', true);
            return;
        }
        if (phoneNumber.trim() === '') {
            createAlert('필수 입력', '전화번호를 입력해주세요', true);
            return;
        }
        if (phoneDigits.length !== 11) {
            createAlert('입력 오류', '전화번호는 010-0000-0000 형식으로 입력해주세요', true);
            return;
        }
        if (memberMatchQuery.isLoading) {
            createAlert('확인 중', '기존 회원 여부 확인이 끝난 뒤 등록해주세요', true);
            return;
        }
        if (!matchedMember && memberName.trim() === '') {
            createAlert('필수 입력', '회원 이름을 입력해주세요', true);
            return;
        }

        const concatStartTime = dayjs(concatDateAndTimeDayjs(selectedDay, startTime.value));
        const concatEndTime = dayjs(concatDateAndTimeDayjs(selectedDay, endTime.value));
        const refineStartTime = dayjs(concatStartTime).toISOString();
        const refineEndTime =
            dayjs(concatEndTime).format('HH:mm') === '00:00'
                ? dayjs(concatEndTime).add(1, 'day').toISOString()
                : dayjs(concatEndTime).toISOString();

        if (isSameDate(dayjs(refineStartTime), dayjs(refineEndTime))) {
            createAlert('입력 오류', '시작 시간과 완료 시간이 같습니다!\n다시 한번 확인해주세요!', '확인', () => {});
            return;
        }

        if (dayjs(refineStartTime).isAfter(dayjs(refineEndTime))) {
            createAlert(
                '입력 오류',
                '시작 시간이 완료 시간보다 늦을 수  없습니다!\n다시 한번 확인해주세요!',
                '확인',
                () => {}
            );
            return;
        }

        createNewOpportunity.mutate({ refineStartTime, refineEndTime });
    };

    useEffect(() => {
        if (startTime) {
            const add50Min: Dayjs = dayjs(startTime.value).add(50, 'minute');

            if (
                add50Min.isAfter(dayjs(startTime.value).endOf('day')) ||
                isSameDate(add50Min, dayjs(startTime.value).endOf('day'))
            ) {
                setEndTime({
                    value: dayjs().endOf('day').add(1, 'millisecond'),
                    label: dayjs().endOf('day').add(1, 'millisecond').format('A hh:mm'),
                });
            } else {
                setEndTime({ value: dayjs(add50Min), label: dayjs(add50Min).format('A hh:mm') });
            }
        }
    }, [startTime]);

    useEffect(() => {
        const timeSlots = [];
        const start = dayjs().startOf('day').hour(0).minute(0);
        for (let i = 0; i < 144; i++) {
            const time = start.add(i * 10, 'minute');
            timeSlots.push({ value: time, label: time.format('A hh:mm') });
        }
        setTimeList(timeSlots);
    }, []);

    return (
        <Col style={{ paddingLeft: 101, paddingTop: 56, paddingRight: 101, paddingBottom: 72 }}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center' }}>
                    <div
                        style={{ cursor: 'pointer', marginRight: 16, display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                            navigate('/schedule', {
                                state: { calendarType, calendarSelectedDay, calendarScrollY },
                                replace: true,
                            });
                        }}
                    >
                        <SvgIcon name={'SvgLeftStrokeArrow'} size={20} stroke={colors.black} fill={colors.trans} />
                    </div>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4, fontSize: 30 }}>{'신규기회 등록하기'}</Subtitle1Bold>
                    <Body3Regular style={{ color: colors.LAVEL_1, marginLeft: 18, fontSize: 16 }}>
                        {'상담, OT, 체험 일정을 캘린더에 남깁니다.'}
                    </Body3Regular>
                </Row>

                <ColorSquareButton
                    isGradient={true}
                    customStyle={{ width: 154, height: 44, opacity: createNewOpportunity.isLoading ? 0.6 : 1 }}
                    text={createNewOpportunity.isLoading ? '등록 중' : '신규기회 등록'}
                    size="middle"
                    callBack={onClickCreateOpportunity}
                />
            </Row>

            <Row
                style={{
                    marginTop: 22,
                    border: '1px solid #DCE6F8',
                    backgroundColor: '#F8FAFC',
                    borderRadius: 12,
                    padding: '16px 18px',
                    color: '#5E7093',
                    fontSize: 15,
                    lineHeight: '23px',
                    fontWeight: 700,
                    alignItems: 'center',
                }}
            >
                신규기회는 캘린더와 클로징률 계산에 반영되며 수강권 차감이나 정산에는 연결되지 않습니다.
            </Row>

            <Row style={{ marginTop: 44, alignItems: 'flex-start', gap: 64 }}>
                <Col style={sectionStyle}>
                    <div style={sectionHeaderStyle}>
                        <span style={sectionNumberStyle}>1</span>
                        <Col>
                            <Subtitle1Bold style={{ color: colors.LAVEL_4, fontSize: 24 }}>회원 정보</Subtitle1Bold>
                        </Col>
                    </div>

                    <Col>
                        <FieldLabel
                            label="회원 확인용 전화번호"
                            help="입력 즉시 기존 센터 회원 여부를 확인합니다."
                        />
                        <input
                            ref={phoneInputRef}
                            placeholder="010-0000-0000"
                            value={phoneNumber}
                            maxLength={13}
                            onChange={(e) => {
                                const rawValue = e.target.value;
                                const cursor = e.target.selectionStart ?? rawValue.length;
                                const formatted = formatNewOpportunityPhoneNumber(rawValue);
                                setPhoneNumber(formatted);
                                requestAnimationFrame(() => {
                                    const el = phoneInputRef.current;
                                    if (!el) return;
                                    const pos = calculateCursorAfterFormat(rawValue, cursor, formatted);
                                    try {
                                        el.setSelectionRange(pos, pos);
                                    } catch {
                                        // ignore
                                    }
                                });
                            }}
                            style={inputStyle}
                        />
                        {phoneDigits.length === 11 && (
                            <div
                                style={{
                                    width: FORM_FIELD_WIDTH,
                                    marginTop: 12,
                                    borderRadius: 12,
                                    padding: '14px 16px',
                                    boxSizing: 'border-box',
                                    border: matchedMember ? '1px solid #BFDBFE' : '1px solid #E5E7EB',
                                    backgroundColor: matchedMember ? '#EFF6FF' : '#F9FAFB',
                                    color: matchedMember ? '#1D4ED8' : '#6B7280',
                                    fontSize: 15,
                                    lineHeight: '22px',
                                    fontWeight: 700,
                                }}
                            >
                                {memberMatchQuery.isLoading
                                    ? '기존 회원 여부를 확인하고 있습니다.'
                                    : matchedMember
                                    ? `${matchedMember.name || '이 회원'}님이 이미 센터에 등록되어 있습니다. 신규기회는 기존 회원에게 할당됩니다.`
                                    : '같은 전화번호의 센터 회원이 없어 신규 퍼블릭 회원으로 등록됩니다.'}
                            </div>
                        )}
                    </Col>

                    {!matchedMember && (
                        <Col style={fieldBlockStyle}>
                            <FieldLabel label="회원 이름" type="conditional" help="신규 퍼블릭 회원으로 등록될 때만 입력합니다. 최대 5글자입니다." />
                            <input
                                placeholder="회원 이름"
                                value={memberName}
                                maxLength={5}
                                onChange={(e) => setMemberName(limitMemberName(e.target.value))}
                                style={inputStyle}
                            />
                        </Col>
                    )}

                    {matchedMember && (
                        <div
                            style={{
                                marginTop: 18,
                                width: FORM_FIELD_WIDTH,
                                border: '1px solid #BFDBFE',
                                backgroundColor: '#EFF6FF',
                                borderRadius: 12,
                                padding: '16px 18px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <Body3Regular style={{ color: '#1D4ED8', fontSize: 17, fontWeight: 800 }}>센터 회원으로 연결</Body3Regular>
                                <span
                                    style={{
                                        borderRadius: 999,
                                        backgroundColor: '#DBEAFE',
                                        color: '#1D4ED8',
                                        padding: '4px 8px',
                                        fontSize: 11,
                                        fontWeight: 800,
                                    }}
                                >
                                    기존 회원
                                </span>
                            </Row>
                            <Body3Regular style={{ color: colors.LAVEL_4, fontSize: 17, marginTop: 10 }}>
                                {matchedMember.name || '-'} · {formatNewOpportunityPhoneNumber(phoneNumber)}
                            </Body3Regular>
                        </div>
                    )}

                    <Col style={fieldBlockStyle}>
                        <FieldLabel label="공유 메모" type="optional" help="강사와 센터 관리자가 같이 볼 상담 내용, 다음 액션, 특이사항입니다." />
                        <textarea
                            placeholder="상담 내용, 다음 액션, 특이사항"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            style={{
                                ...inputStyle,
                                height: 132,
                                paddingTop: 16,
                                resize: 'vertical',
                                lineHeight: '25px',
                            }}
                        />
                    </Col>

                    <label
                        style={{
                            width: FORM_FIELD_WIDTH,
                            marginTop: 24,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 12,
                            padding: '16px 18px',
                            border: '1px solid #DCE6F8',
                            borderRadius: 12,
                            backgroundColor: '#F7FAFF',
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isSendAlimtalkToMember}
                            onChange={(e) => setIsSendAlimtalkToMember(e.target.checked)}
                            style={{ marginTop: 3 }}
                        />
                        <Col>
                            <Body3Regular style={{ color: colors.LAVEL_4, fontSize: 17, fontWeight: 800 }}>{'앱 미사용 회원에게 알림톡 발송'}</Body3Regular>
                            <CaptionLight style={{ color: colors.LAVEL_2, marginTop: 6 }}>
                                {'회원 가입 후 앱에서 등록된 일정을 확인할 수 있어요.'}
                            </CaptionLight>
                        </Col>
                    </label>
                </Col>

                <Col style={sectionStyle}>
                    <div style={sectionHeaderStyle}>
                        <span style={sectionNumberStyle}>2</span>
                        <Col>
                            <Subtitle1Bold style={{ color: colors.LAVEL_4, fontSize: 24 }}>일정 정보</Subtitle1Bold>
                        </Col>
                    </div>

                    <Col>
                        <FieldLabel label="신규기회 유형" help="기본 유형은 OT, 상담, 플러팅, 지인소개, 체험입니다." />
                        <select style={inputStyle} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                            <option value="">유형을 선택해주세요</option>
                            {activeCategories.map((category) => (
                                <option key={getId(category)} value={getId(category)}>
                                    {category.name}
                                </option>
                            ))}
                        </select>

                        <Row style={{ width: FORM_FIELD_WIDTH, marginTop: 14, alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {activeCategories.slice(0, 5).map((category) => {
                                const isSelected = getId(category) === categoryId;
                                return (
                                    <button
                                        key={getId(category)}
                                        type="button"
                                        onClick={() => setCategoryId(getId(category))}
                                        style={{
                                            minWidth: 86,
                                            height: 36,
                                            padding: '0 16px',
                                            borderRadius: 999,
                                            border: isSelected ? `1px solid ${colors.BLUE_500}` : '1px solid #A6BFEF',
                                            backgroundColor: isSelected ? '#EDF4FF' : colors.WHITE_50,
                                            color: isSelected ? colors.BLUE_500 : '#3869C9',
                                            fontSize: 15,
                                            fontWeight: 800,
                                            lineHeight: '36px',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        {category.name}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>

                    <Col style={{ width: FORM_FIELD_WIDTH, marginTop: 34 }}>
                        <FieldLabel label="담당 강사" />
                        {coachOption && (
                            <InputSquareDropDown
                                selectedGender={coach}
                                genderOptions={coachOption}
                                handleGenderSelect={handleCoachSelect}
                                customStyle={{ width: FORM_FIELD_WIDTH, marginTop: 14 }}
                                zIndex={110}
                            />
                        )}
                    </Col>

                    <Col style={{ width: FORM_FIELD_WIDTH, marginTop: 34 }}>
                        <FieldLabel label="날짜" />
                        <InputSquareDropDown
                            selectedGender={coach}
                            genderOptions={coachOption}
                            handleGenderSelect={handleCoachSelect}
                            customStyle={{ width: FORM_FIELD_WIDTH, marginTop: 14 }}
                            isJustClick={true}
                            justClickString={dayjs(selectedDay).format('YYYY년 M월 D일(ddd)')}
                            justClickCallBack={() => setIsCalendar(true)}
                            zIndex={90}
                        />
                    </Col>

                    <Col style={{ width: FORM_FIELD_WIDTH, marginTop: 34 }}>
                        <FieldLabel label="시간" help="기본 50분으로 잡히며, 아래 버튼으로 종료 시간을 빠르게 조정할 수 있습니다." />

                        <Row style={{ width: FORM_FIELD_WIDTH, alignItems: 'center', justifyContent: 'space-between', marginTop: 18 }}>
                            {timeList && (
                                <InputSquareDropDown
                                    selectedGender={startTime}
                                    genderOptions={timeList}
                                    handleGenderSelect={handleStartTimeSelect}
                                    isDefaultScroll={true}
                                />
                            )}

                            <Body3Regular style={{ color: colors.BLUE_500 }}>~</Body3Regular>
                            {timeList && (
                                <InputSquareDropDown
                                    selectedGender={endTime}
                                    genderOptions={timeList}
                                    handleGenderSelect={handleEndTimeSelect}
                                    isDefaultScroll={true}
                                />
                            )}
                        </Row>

                        <Row
                            style={{
                                width: FORM_FIELD_WIDTH,
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                                marginTop: 18,
                            }}
                        >
                            <WhiteRoundButton customStyle={{ width: 68, height: 30 }} text="-30분" callBack={() => onClickSubTime(30)} size="tiny" />
                            <WhiteRoundButton customStyle={{ width: 68, height: 30 }} text="-20분" callBack={() => onClickSubTime(20)} size="tiny" />
                            <WhiteRoundButton customStyle={{ width: 68, height: 30 }} text="-10분" callBack={() => onClickSubTime(10)} size="tiny" />
                            <WhiteRoundButton customStyle={{ width: 68, height: 30 }} text="+10분" callBack={() => onClickAddTime(10)} size="tiny" />
                            <WhiteRoundButton customStyle={{ width: 68, height: 30 }} text="+20분" callBack={() => onClickAddTime(20)} size="tiny" />
                            <WhiteRoundButton customStyle={{ width: 68, height: 30 }} text="+30분" callBack={() => onClickAddTime(30)} size="tiny" />
                        </Row>
                    </Col>
                </Col>
            </Row>

            {isCalendar && (
                <Calendar
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    setIsCalendar={setIsCalendar}
                    isPrevMonth={true}
                    isNextMonth={true}
                    style={{
                        width: 420,
                        height: 370,
                        borderRadius: 24,
                        backgroundColor: colors.WHITE_50,
                        boxShadow: '1px 1px 8px 0px #2B529D2E',
                        position: 'absolute',
                        zIndex: 1999,
                        marginTop: 190,
                        marginLeft: 500,
                    }}
                />
            )}
        </Col>
    );
};

export default CreateNewOpportunitySchedule;
